import _ from 'underscore';
import { useEffect, useContext, useCallback, useMemo, useState } from 'react';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { AssetEventFeature, useLogRecommendationsApiError } from '@bingads-webui-campaign-react/copilot-common';
import { usePostAssetEvent, AssetChannel, AssetScenario } from '@bingads-webui-react/copilot-event-provider';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { RecommendationContext } from './recommendation-context';
import { getEmptyRecommendationsContext } from './util';

export function useCopilotRecommendations({
  campaignId,
  serverContext,
  editMode,
  entityId,
  instrumentation,
  activityLogger,
}) {
  const activity = useContext(ActivityContext);

  const logRecommendationsAPIError = useLogRecommendationsApiError(activity || activityLogger);

  const logTrace = useMemo(() => {
    if (activity && activity.trace) {
      return activity.trace;
    }
    if (activityLogger && activityLogger.trace) {
      return activityLogger.trace;
    }
    if (instrumentation && instrumentation.AdsCopilot) {
      const scenario = instrumentation.AdsCopilot.create();
      return scenario.createActivity('AssetEvent').trace;
    }
    return null;
  }, [activity, activityLogger, instrumentation]);

  const [, setRecommendationContext] = useContext(RecommendationContext);

  const { Permissions, DynamicPermissions } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const [isRegionRestricted, setIsRegionRestricted] = useState(true);
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Recommendations',
      permissions,
      serverContext,
    }).then(setIsRegionRestricted);
  }, [permissions, serverContext]);

  const postAssetEventLog = usePostAssetEvent({
    shouldLog: _.isFunction(logTrace) && !isRegionRestricted,
    serverContext,
    channel: AssetChannel.WEB_UI,
    feature: AssetEventFeature.RSA,
    scenario: editMode ? AssetScenario.EDIT_WORK_FLOW : AssetScenario.CREATE_WORK_FLOW,
    campaignId,
    assetGroupId: editMode ? entityId : undefined,
    logTrace,
  });

  useEffect(() => {
    // create the postAssetEventLog function and save it in the context
    setRecommendationContext({ postAssetEventLog });
  }, [postAssetEventLog, setRecommendationContext]);

  const resetRecommendations = useCallback((newFinalUrl) => {
    setRecommendationContext(getEmptyRecommendationsContext(newFinalUrl));
  }, [setRecommendationContext]);

  return {
    logRecommendationsAPIError,
    resetRecommendations,
  };
}
